import { Cake } from 'lucide-react';
import type { BirthdayAnniversaryPerson } from '@hygge/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useBirthdaysAnniversaries } from '@/hooks/use-dashboard';
import { WidgetCard } from '../widget-card';
import { PersonRow } from '../person-row';
import { ExpandableList } from '../expandable-list';
import { asArray } from '../widget-format';

interface CelebrationRow {
  key: string;
  person: BirthdayAnniversaryPerson;
  subline: string;
}

/**
 * `birthdaysAnniversaries` widget — today's celebrants across the company: new starters
 * (👋), birthdays (🎂) and work anniversaries (🎉), in that order. View only; each row
 * links to the person's profile via `PersonRow`.
 */
export function BirthdaysAnniversariesWidget() {
  const { data, isLoading, isError } = useBirthdaysAnniversaries();
  const formatName = useEmployeeNameFormatter();

  // Sort each group by the *displayed* name so ordering honors the Name Display
  // setting (first/last vs last/first).
  const byName = (a: BirthdayAnniversaryPerson, b: BirthdayAnniversaryPerson) =>
    formatName(a).localeCompare(formatName(b));

  const rows: CelebrationRow[] = data
    ? [
        ...[...asArray(data.newStarters)].sort(byName).map((p) => ({
          key: `new-${p.id}`,
          person: p,
          subline: '👋 First day today — welcome!',
        })),
        ...[...asArray(data.birthdays)].sort(byName).map((p) => ({
          key: `bday-${p.id}`,
          person: p,
          subline: '🎂 Birthday today',
        })),
        ...[...asArray(data.anniversaries)].sort(byName).map((p) => ({
          key: `anniv-${p.id}`,
          person: p,
          subline: Number.isFinite(p.years)
            ? `🎉 ${p.years}-year anniversary`
            : '🎉 Work anniversary today',
        })),
      ]
    : [];

  return (
    <WidgetCard
      title="Birthdays, Anniversaries & New Starters"
      icon={Cake}
      caption="Shown for today only — visible to everyone."
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Couldn&apos;t load today&apos;s celebrations.
        </p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Nothing to celebrate today — check back tomorrow.
        </p>
      ) : (
        <ExpandableList
          items={rows}
          getKey={(r) => r.key}
          renderItem={(r) => <PersonRow employee={r.person} subline={r.subline} />}
        />
      )}
    </WidgetCard>
  );
}
